import { useCallback } from 'react'

import { chartsLinkState } from 'features/ask-yogi/features/conversation/utils/charts-link-state'
import { mintYogiQLLinkKey } from 'features/ask-yogi/features/conversation/utils/yogiql-link-key'
import type { AskYogiService } from 'features/ask-yogi/service'
import { useWhenEnhancedDialect } from 'features/charts-v2/hooks'
import {
  isYogiQLStoredPanel,
  type StoredPanel,
} from 'features/charts-v2/types/stored-panel'

/**
 * Mints a chat chart's "View in charts" key. Under `enhanced_dialect`, the
 * charts page-state of each YogiQL panel stored as the request the chat drew;
 * off it, for a legacy panel, or when the save fails, the `legacy` hash.
 */
export function useChartsLinkKey(
  projectId: string,
  service: AskYogiService,
): (
  panels: StoredPanel[],
  activePanelId: string | undefined,
  legacy: () => Promise<string>,
) => Promise<string> {
  const whenEnhancedDialect = useWhenEnhancedDialect()
  return useCallback(
    async (panels, activePanelId, legacy) => {
      if (!panels.some(isYogiQLStoredPanel) || !(await whenEnhancedDialect())) {
        return legacy()
      }
      const state = chartsLinkState(panels, activePanelId)
      if (!state) return legacy()
      return mintYogiQLLinkKey(
        'charts',
        state,
        (s) => service.saveLinkState(projectId, 'charts', s),
        legacy,
      )
    },
    [projectId, service, whenEnhancedDialect],
  )
}
